import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

// Hover-to-reveal info popover anchored to the trigger icon. Used for inline
// disclaimers / explanations on toggles, KPIs, column headers, and panels.
// Opens on mouseenter, closes on mouseleave (with a tiny grace window so the
// pointer can move into the popover to read / copy).
export function InfoButton({
  children,
  label,
  size = 14,
  align = "right",
  width = 360,
}: {
  children: ReactNode;
  label?: string;
  size?: number;
  align?: "left" | "right";
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => cancelClose(), []);

  return (
    <span
      ref={ref}
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      onFocus={() => { cancelClose(); setOpen(true); }}
      onBlur={scheduleClose}
      style={{ position: "relative", display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}
    >
      <span
        aria-label={label ?? "More information"}
        role="img"
        tabIndex={0}
        style={{
          background: "transparent",
          padding: 2,
          cursor: "help",
          color: open ? "var(--color-teal)" : "var(--fg-3)",
          display: "inline-flex",
          alignItems: "center",
          fontSize: size,
          lineHeight: 1,
        }}
      >
        <i className="ti ti-info-circle" style={{ fontSize: size }} />
      </span>
      {open && (
        <div
          role="tooltip"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            [align]: 0,
            zIndex: 30,
            minWidth: 240,
            width: "max-content",
            maxWidth: `min(${width}px, calc(100vw - 32px))`,
            background: "var(--bg-elevated, #fff)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
            padding: "10px 12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.45,
            color: "var(--fg-1)",
            textTransform: "none",
            letterSpacing: "normal",
            whiteSpace: "normal",
          }}
        >
          {children}
        </div>
      )}
    </span>
  );
}
